// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useCart } from '../contexts/CartContext';
import { Tag, Clock, Copy, Check, ShoppingBag, Percent, DollarSign, Truck } from 'lucide-react';

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

const DISCOUNT_ICONS = {
  percent:      Percent,
  fixed:        DollarSign,
  bogo:         ShoppingBag,
  free_delivery: Truck,
};

function CouponCard({ coupon, onApply }) {
  const [copied, setCopied] = useState(false);
  const Icon = DISCOUNT_ICONS[coupon.discount_type] || Tag;
  const daysLeft = coupon.valid_until
    ? Math.ceil((new Date(coupon.valid_until) - Date.now()) / 86400000)
    : null;

  const handleCopy = () => {
    if (coupon.code) {
      navigator.clipboard.writeText(coupon.code).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const discountText = (() => {
    if (coupon.discount_type === 'percent')       return `${coupon.discount_value}% off`;
    if (coupon.discount_type === 'fixed')         return `${fmt(coupon.discount_value * 100)} off`;
    if (coupon.discount_type === 'bogo')          return 'Buy 1 Get 1 Free';
    if (coupon.discount_type === 'free_delivery') return 'Free Delivery';
    return 'Special Deal';
  })();

  const colorMap = {
    percent:       { bg: 'linear-gradient(135deg, #16A34A, #15803D)', badge: '#BBF7D0', badgeText: '#15803D' },
    fixed:         { bg: 'linear-gradient(135deg, #F97316, #EA580C)', badge: '#FED7AA', badgeText: '#C2410C' },
    bogo:          { bg: 'linear-gradient(135deg, #8B5CF6, #7C3AED)', badge: '#EDE9FE', badgeText: '#6D28D9' },
    free_delivery: { bg: 'linear-gradient(135deg, #3B82F6, #2563EB)', badge: '#DBEAFE', badgeText: '#1D4ED8' },
  };
  const colors = colorMap[coupon.discount_type] || colorMap.fixed;

  return (
    <div style={{
      background: '#fff', borderRadius: 16, overflow: 'hidden',
      border: '1px solid #BBF7D0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      display: 'flex',
    }}>
      {/* Left: colored stripe */}
      <div style={{
        width: 80, background: colors.bg, flexShrink: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '16px 8px', gap: 6,
      }}>
        <Icon size={28} color="#fff" />
        <div style={{ fontSize: 13, fontWeight: 900, color: '#fff', textAlign: 'center', lineHeight: 1.2 }}>
          {discountText}
        </div>
      </div>

      {/* Right: content */}
      <div style={{ flex: 1, padding: '16px 18px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#14532D', marginBottom: 4 }}>{coupon.title}</div>
            {coupon.description && (
              <div style={{ fontSize: 13, color: '#64748B', marginBottom: 8 }}>{coupon.description}</div>
            )}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {coupon.min_order_cents > 0 && (
                <span style={{ fontSize: 11, background: '#F0FDF4', color: '#16A34A', padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>
                  Min. {fmt(coupon.min_order_cents)}
                </span>
              )}
              {daysLeft !== null && daysLeft <= 7 && (
                <span style={{ fontSize: 11, background: '#FEF3C7', color: '#D97706', padding: '2px 8px', borderRadius: 6, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={11} /> {daysLeft <= 0 ? 'Expires today' : `${daysLeft}d left`}
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          {coupon.code && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#F0FDF4', borderRadius: 8, padding: '6px 12px',
              border: '1.5px dashed #86EFAC',
            }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: '#15803D', letterSpacing: 1.5 }}>{coupon.code}</span>
              <button
                onClick={handleCopy}
                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', color: copied ? '#16A34A' : '#94A3B8' }}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          )}
          <button
            className="btn btn-primary btn-sm"
            onClick={() => onApply(coupon.code)}
          >
            Apply to Cart
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CouponsPage() {
  const { applyCoupon, setIsOpen } = useCart();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applied, setApplied] = useState(null);
  const [error, setError]     = useState('');

  useEffect(() => {
    supabase.from('grocery_coupons')
      .select('*')
      .eq('active', true)
      .order('created_at', { ascending: false })
      .then(({ data }) => { setCoupons(data || []); setLoading(false); });
  }, []);

  const handleApply = async (code) => {
    if (!code) return;
    setError(''); setApplied(null);
    try {
      const coupon = await applyCoupon(code);
      setApplied(coupon.title);
      setIsOpen(true);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="page-container" style={{ paddingTop: 32 }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <div style={{ fontSize: 52, marginBottom: 12 }}>🏷️</div>
        <h1 style={{ fontSize: 32, fontWeight: 900, color: '#14532D', marginBottom: 8 }}>Digital Coupons</h1>
        <p style={{ fontSize: 16, color: '#64748B', maxWidth: 480, margin: '0 auto' }}>
          Exclusive deals just for you. Apply coupon codes at checkout or click "Apply to Cart" below.
        </p>
      </div>

      {applied && (
        <div style={{ background: '#DCFCE7', color: '#16A34A', padding: '12px 20px', borderRadius: 12, marginBottom: 24, textAlign: 'center', fontSize: 14, fontWeight: 700 }}>
          ✓ "{applied}" applied to your cart!
          <button className="btn btn-primary btn-sm" style={{ marginLeft: 12 }} onClick={() => { navigate('/cart'); }}>
            View Cart
          </button>
        </div>
      )}
      {error && (
        <div style={{ background: '#FEE2E2', color: '#DC2626', padding: '12px 20px', borderRadius: 12, marginBottom: 24, textAlign: 'center', fontSize: 14 }}>
          {error}
        </div>
      )}

      {coupons.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#94A3B8' }}>
          No active coupons right now. Check back soon!
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {coupons.map(c => (
            <CouponCard key={c.id} coupon={c} onApply={handleApply} />
          ))}
        </div>
      )}

      {/* Manual code entry */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, marginTop: 32, border: '1px solid #BBF7D0', textAlign: 'center' }}>
        <Tag size={24} color="#16A34A" style={{ marginBottom: 10 }} />
        <h3 style={{ fontSize: 17, fontWeight: 700, color: '#14532D', marginBottom: 6 }}>Have a coupon code?</h3>
        <p style={{ fontSize: 13, color: '#64748B', marginBottom: 14 }}>Enter your code during checkout or apply it directly to your cart.</p>
        <button className="btn btn-outline" onClick={() => { setIsOpen(true); }}>
          Open Cart to Apply Code
        </button>
      </div>
    </div>
  );
}
